import { type RovoUserSettings } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export type UseRovoUserSettingsParams = {
	enabled: boolean;
} & AssistanceServiceInputParam;

type UseRovoUserSettingsHookParams = {
	enabled: boolean;
} & AssistanceServiceInjectedParams;

export type UseRovoUserSettingsResult = {
	settings: RovoUserSettings | null;
	isLoading: boolean;
	isFetching: boolean;
	isSubmitting: boolean;
	refetch: () => Promise<RovoUserSettings | null>;
	updateSettings: (
		payload: Partial<RovoUserSettings>,
		callbacks?:
			| {
					onSuccess?: ((data: RovoUserSettings) => void) | undefined;
					onError?: (error: ApiError) => void;
			  }
			| undefined,
	) => Promise<RovoUserSettings | undefined>;
};

/**
 * Hook for fetching and managing Rovo user settings.
 */
export const useRovoUserSettings: (props: UseRovoUserSettingsParams) => UseRovoUserSettingsResult =
	createHookWithAssistanceService(
		({
			enabled,
			assistanceServiceInjectedParams: { assistanceService },
		}: UseRovoUserSettingsHookParams): UseRovoUserSettingsResult => {
			const {
				data: settings,
				isFetching,
				refetch,
			} = useQuery<RovoUserSettings>({
				queryKey: ['rovoUserSettings'],
				enabled,
				queryFn: async () => {
					return assistanceService.getRovoUserSettings();
				},
			});

			const { mutate: updateSettings, isSubmitting } = useMutation<
				Partial<RovoUserSettings>,
				RovoUserSettings
			>({
				mutationFn: async (newSettings) => {
					return assistanceService.updateRovoUserSettings(newSettings);
				},
				onSuccess: async () => {
					await refetch();
				},
			});

			const isInitialFetching = isFetching && settings === null;

			return {
				settings,
				isLoading: isInitialFetching || isSubmitting,
				isFetching: isInitialFetching,
				isSubmitting,
				refetch,
				updateSettings,
			};
		},
	);
